import { FormEvent, Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { platformApi } from '../features/platform/public'

type SubmissionStatus = 'submitted' | 'in_review' | 'revision_requested' | 'approved' | 'rejected'
type ReviewDecision = 'approved' | 'revision_requested' | 'rejected'
type SubmissionKind = 'link' | 'text'
type SubmissionStage = 'draft' | 'published'

type VersionRow = {
  id: string
  version_number: number
  stage: SubmissionStage
  kind: SubmissionKind
  content_value: string
  creator_note: string | null
  created_at: string
}

type ReviewRow = {
  id: string
  submission_version_id: string
  decision: ReviewDecision
  comment: string | null
  revision_due_at: string | null
  created_at: string
}

type SubmissionRow = {
  id: string
  tenant_id: string
  campaign_id: string
  campaign_creator_id: string
  creator_task_id: string
  deliverable_id: string
  status: SubmissionStatus
  current_version: number
  created_at: string
  updated_at: string
  task: { id: string; status: string; due_at: string | null } | null
  campaign_creator: {
    id: string
    creator: {
      full_name: string
      primary_platform: string | null
      primary_handle: string | null
    } | null
  } | null
  deliverable: { id: string; name: string; type: string } | null
  versions: VersionRow[]
  reviews: ReviewRow[]
}

type ExistingException = {
  id: string
  type: string
  creator_task_id: string | null
}

const statusLabels: Record<SubmissionStatus, string> = {
  submitted: 'Chờ duyệt',
  in_review: 'Đang duyệt',
  revision_requested: 'Đã yêu cầu sửa',
  approved: 'Đã duyệt',
  rejected: 'Từ chối',
}

const decisionLabels: Record<ReviewDecision, string> = {
  approved: 'Approve',
  revision_requested: 'Request revision',
  rejected: 'Reject',
}

const stageLabels: Record<SubmissionStage, string> = {
  draft: 'Draft',
  published: 'Post link',
}

const commentTemplates = [
  'Nội dung chưa đúng brief. Vui lòng chỉnh sửa theo yêu cầu đã giao.',
  'Link không mở được hoặc chưa cấp quyền xem.',
  'Thiếu thông tin bắt buộc trong nội dung nộp.',
  'Định dạng hoặc thời lượng chưa đúng yêu cầu.',
]

function currentVersionOf(submission: SubmissionRow) {
  return submission.versions.find((version) => version.version_number === submission.current_version)
    ?? submission.versions[submission.versions.length - 1]
    ?? null
}

function previousVersionOf(submission: SubmissionRow) {
  const current = currentVersionOf(submission)
  if (!current) return null
  return submission.versions.find((version) => version.version_number === current.version_number - 1) ?? null
}

function isReviewable(submission: SubmissionRow) {
  const current = currentVersionOf(submission)
  return current?.stage === 'draft' && ['submitted', 'in_review'].includes(submission.status)
}

function isSafeHttpUrl(value: string) {
  try {
    const url = new URL(value)
    return url.protocol === 'http:' || url.protocol === 'https:'
  } catch {
    return false
  }
}

function formatDate(value?: string | null) {
  return value ? new Date(value).toLocaleString('vi-VN') : '—'
}

export default function ApprovalQueue({ campaignId }: { campaignId: string }) {
  const [submissions, setSubmissions] = useState<SubmissionRow[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'needs_review' | SubmissionStatus | 'all'>('needs_review')
  const [deliverableFilter, setDeliverableFilter] = useState('all')
  const [reviewingId, setReviewingId] = useState('')
  const [decision, setDecision] = useState<ReviewDecision>('approved')
  const [comment, setComment] = useState('')
  const [revisionDue, setRevisionDue] = useState('')

  const syncOverdueExceptions = useCallback(async (rows: SubmissionRow[]) => {
    const existingResult = await platformApi.approvals.listOpenExceptions(campaignId, ['submission_overdue', 'revision_overdue'])
    if (existingResult.error) throw new Error(existingResult.error.message)
    const existing = (existingResult.data ?? []) as ExistingException[]
    const existingByKey = new Map(existing.map((item) => [`${item.type}:${item.creator_task_id}`, item]))
    const now = Date.now()

    for (const submission of rows) {
      const version = currentVersionOf(submission)
      const dueAt = submission.task?.due_at
      const creatorName = submission.campaign_creator?.creator?.full_name ?? 'Creator'
      const deliverableName = submission.deliverable?.name ?? 'deliverable'
      const desired: Array<{ type: string; summary: string; details: Record<string, unknown> }> = []

      if (version?.stage === 'draft' && dueAt && new Date(version.created_at).getTime() > new Date(dueAt).getTime()) {
        desired.push({
          type: 'submission_overdue',
          summary: `${creatorName} nộp “${deliverableName}” sau deadline`,
          details: {
            submission_id: submission.id,
            version_number: version.version_number,
            due_at: dueAt,
            submitted_at: version.created_at,
          },
        })
      }

      if (submission.status === 'revision_requested' && dueAt && new Date(dueAt).getTime() < now) {
        desired.push({
          type: 'revision_overdue',
          summary: `${creatorName} quá hạn revision cho “${deliverableName}”`,
          details: {
            submission_id: submission.id,
            current_version: submission.current_version,
            revision_due_at: dueAt,
          },
        })
      }

      for (const item of desired) {
        const key = `${item.type}:${submission.creator_task_id}`
        const existingException = existingByKey.get(key)
        if (existingException) {
          const updateResult = await platformApi.approvals.updateException(existingException.id, {
            severity: 'high',
            summary: item.summary,
            details: item.details,
          })
          if (updateResult.error) throw new Error(updateResult.error.message)
          continue
        }

        const insertResult = await platformApi.approvals.createException({
          tenant_id: submission.tenant_id,
          campaign_id: submission.campaign_id,
          campaign_creator_id: submission.campaign_creator_id,
          creator_task_id: submission.creator_task_id,
          type: item.type,
          severity: 'high',
          status: 'open',
          summary: item.summary,
          details: item.details,
        })

        if (insertResult.error) throw new Error(insertResult.error.message)
        existingByKey.set(key, insertResult.data as ExistingException)
      }
    }
  }, [campaignId])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    const result = await platformApi.approvals.listSubmissions(campaignId)

    if (result.error) {
      setError(result.error.message)
      setLoading(false)
      return
    }

    const rows = ((result.data ?? []) as unknown as SubmissionRow[]).map((submission) => {
      const versions = [...(submission.versions ?? [])].sort((a, b) => a.version_number - b.version_number)
      const versionNumberById = new Map(versions.map((version) => [version.id, version.version_number]))
      const reviews = [...(submission.reviews ?? [])].sort((a, b) => {
        const versionOrder = (versionNumberById.get(b.submission_version_id) ?? 0) - (versionNumberById.get(a.submission_version_id) ?? 0)
        if (versionOrder !== 0) return versionOrder
        const timeOrder = new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        return timeOrder !== 0 ? timeOrder : b.id.localeCompare(a.id)
      })
      return { ...submission, versions, reviews }
    })

    setSubmissions(rows)
    try {
      await syncOverdueExceptions(rows)
    } catch (syncError) {
      setError(syncError instanceof Error ? syncError.message : 'Không thể đồng bộ exception quá hạn.')
    }
    setLoading(false)
  }, [campaignId, syncOverdueExceptions])

  useEffect(() => { void load() }, [load])

  const deliverables = useMemo(() => {
    const map = new Map<string, string>()
    submissions.forEach((submission) => {
      if (submission.deliverable) map.set(submission.deliverable.id, submission.deliverable.name)
    })
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1], 'vi'))
  }, [submissions])

  const filtered = useMemo(() => {
    const keyword = search.trim().toLocaleLowerCase('vi')
    return submissions.filter((submission) => {
      const creator = submission.campaign_creator?.creator
      const matchesSearch = !keyword || [creator?.full_name, creator?.primary_handle, submission.deliverable?.name]
        .filter(Boolean)
        .some((value) => String(value).toLocaleLowerCase('vi').includes(keyword))
      const matchesDeliverable = deliverableFilter === 'all' || submission.deliverable_id === deliverableFilter
      const matchesStatus = statusFilter === 'all'
        || (statusFilter === 'needs_review' && isReviewable(submission))
        || submission.status === statusFilter
      return matchesSearch && matchesDeliverable && matchesStatus
    })
  }, [deliverableFilter, search, statusFilter, submissions])

  const reviewing = useMemo(
    () => submissions.find((submission) => submission.id === reviewingId) ?? null,
    [reviewingId, submissions],
  )

  function openReview(submission: SubmissionRow, nextDecision: ReviewDecision = 'approved') {
    if (!isReviewable(submission)) {
      setError('Chỉ draft hiện tại ở trạng thái chờ duyệt mới có thể review.')
      return
    }
    setReviewingId(submission.id)
    setDecision(nextDecision)
    setComment('')
    setRevisionDue('')
    setError('')
    setMessage('')
  }

  async function submitReview(event: FormEvent) {
    event.preventDefault()
    if (!reviewing) return
    const current = currentVersionOf(reviewing)
    if (!current || current.stage !== 'draft') {
      setError('Submission hiện tại không phải draft hợp lệ để review.')
      return
    }
    if (decision !== 'approved' && !comment.trim()) {
      setError('Comment là bắt buộc khi yêu cầu sửa hoặc reject.')
      return
    }
    if (decision === 'revision_requested' && revisionDue && new Date(revisionDue).getTime() <= Date.now()) {
      setError('Revision deadline phải nằm trong tương lai.')
      return
    }

    setBusy(true)
    setError('')
    setMessage('')
    const result = await platformApi.approvals.reviewSubmission({
      submissionId: reviewing.id,
      versionNumber: current.version_number,
      decision,
      comment: comment.trim() || null,
      revisionDueAt: decision === 'revision_requested' && revisionDue
        ? new Date(revisionDue).toISOString()
        : null,
    })
    setBusy(false)

    if (result.error) {
      setError(result.error.message)
      return
    }

    setMessage(`Đã ${decisionLabels[decision].toLocaleLowerCase('vi')} draft version ${current.version_number}.`)
    setReviewingId('')
    await load()
  }

  async function markInvalidLink(submission: SubmissionRow, version: VersionRow) {
    const creatorName = submission.campaign_creator?.creator?.full_name ?? 'Creator'
    const existingResult = await platformApi.approvals.findOpenInvalidLinkException(campaignId, submission.creator_task_id)

    if (existingResult.error) {
      setError(existingResult.error.message)
      return
    }
    if ((existingResult.data ?? []).length > 0) {
      setMessage('Task này đã có exception link không hợp lệ đang mở.')
      return
    }

    const insertResult = await platformApi.approvals.createException({
      tenant_id: submission.tenant_id,
      campaign_id: submission.campaign_id,
      campaign_creator_id: submission.campaign_creator_id,
      creator_task_id: submission.creator_task_id,
      type: 'invalid_submission_link',
      severity: 'high',
      status: 'open',
      summary: `${creatorName} nộp link không hợp lệ`,
      details: {
        submission_id: submission.id,
        version_number: version.version_number,
        stage: version.stage,
        content_value: version.content_value,
      },
    })

    if (insertResult.error) setError(insertResult.error.message)
    else setMessage('Đã tạo exception link không hợp lệ; exception không tự đóng khi review.')
  }

  return (
    <article className="panel">
      <div className="panel-header">
        <div><p className="eyebrow">M4 — APPROVAL QUEUE</p><h3>Duyệt draft submission</h3></div>
        <button onClick={() => void load()}>Làm mới</button>
      </div>
      <p className="muted">Draft được review qua RPC có audit; post link đã xuất bản được giữ trong version history nhưng không quay lại hàng chờ duyệt.</p>

      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}

      <div className="form-two-col">
        <label>Tìm creator hoặc deliverable
          <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Tên, handle, deliverable…" />
        </label>
        <label>Trạng thái
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}>
            <option value="needs_review">Cần review draft</option>
            <option value="all">Tất cả</option>
            {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </label>
      </div>
      <label>Deliverable
        <select value={deliverableFilter} onChange={(event) => setDeliverableFilter(event.target.value)}>
          <option value="all">Tất cả deliverable</option>
          {deliverables.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select>
      </label>

      {reviewing && (
        <form className="stack compact-form" onSubmit={submitReview}>
          <div className="panel-header">
            <div>
              <p className="eyebrow">REVIEW DRAFT VERSION {reviewing.current_version}</p>
              <h3>{reviewing.campaign_creator?.creator?.full_name ?? 'Creator'} · {reviewing.deliverable?.name ?? 'Deliverable'}</h3>
            </div>
            <button type="button" onClick={() => setReviewingId('')}>Đóng</button>
          </div>
          <label>Quyết định
            <select value={decision} onChange={(event) => setDecision(event.target.value as ReviewDecision)}>
              {Object.entries(decisionLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>Mẫu comment
            <select value="" onChange={(event) => setComment(event.target.value)}>
              <option value="">— Chọn mẫu —</option>
              {commentTemplates.map((template) => <option key={template} value={template}>{template}</option>)}
            </select>
          </label>
          <label>Comment {decision !== 'approved' && <strong>*</strong>}
            <textarea rows={4} value={comment} onChange={(event) => setComment(event.target.value)} />
          </label>
          {decision === 'revision_requested' && <label>Revision deadline
            <input type="datetime-local" value={revisionDue} onChange={(event) => setRevisionDue(event.target.value)} />
          </label>}
          <div className="form-actions">
            <button type="button" onClick={() => setReviewingId('')}>Hủy</button>
            <button className="primary" disabled={busy}>{busy ? 'Đang lưu review…' : decisionLabels[decision]}</button>
          </div>
        </form>
      )}

      {loading ? <p>Đang tải approval queue…</p> : filtered.length === 0 ? (
        <div className="empty"><h3>Không có submission phù hợp</h3><p>Thử đổi filter hoặc chờ creator nộp draft mới.</p></div>
      ) : (
        <div className="table-wrap"><table><thead><tr><th>Creator</th><th>Deliverable</th><th>Status</th><th>Submitted</th><th>Deadline</th><th>Version</th><th>Review</th></tr></thead><tbody>
          {filtered.map((submission) => {
            const creator = submission.campaign_creator?.creator
            const current = currentVersionOf(submission)
            const previous = previousVersionOf(submission)
            const latestReview = submission.reviews[0]
            const dueAt = submission.task?.due_at
            const isLate = Boolean(current?.stage === 'draft' && dueAt && new Date(current.created_at).getTime() > new Date(dueAt).getTime())
            const canReview = isReviewable(submission)
            const displayStatus = submission.task?.status === 'published' ? 'Đã xuất bản' : statusLabels[submission.status]

            return <Fragment key={submission.id}>
              <tr>
                <td><strong>{creator?.full_name ?? '—'}</strong><small>{[creator?.primary_platform, creator?.primary_handle].filter(Boolean).join(' · ')}</small></td>
                <td>{submission.deliverable?.name ?? '—'}</td>
                <td><span className="status status-configured">{displayStatus}</span></td>
                <td>{formatDate(current?.created_at)}</td>
                <td>{formatDate(dueAt)}<small>{current?.stage === 'published' ? 'Post link' : isLate ? 'Late' : 'On time'}</small></td>
                <td>v{submission.current_version}<small>{current ? stageLabels[current.stage] : '—'}</small></td>
                <td>{canReview ? <div className="row-actions">
                  <button className="primary" onClick={() => openReview(submission, 'approved')}>Review</button>
                  <button onClick={() => openReview(submission, 'revision_requested')}>Revision</button>
                </div> : <span className="muted">Không cần review</span>}</td>
              </tr>
              <tr>
                <td colSpan={7}>
                  <details>
                    <summary>Xem version history và so sánh</summary>
                    {latestReview && <div className="notice">Review draft gần nhất: {decisionLabels[latestReview.decision]} · {formatDate(latestReview.created_at)}{latestReview.comment ? ` — ${latestReview.comment}` : ''}</div>}
                    <div className="two-column">
                      <VersionCard title={`Current v${current?.version_number ?? '—'}`} version={current} onInvalid={() => current && void markInvalidLink(submission, current)} />
                      <VersionCard title={previous ? `Previous v${previous.version_number}` : 'Previous'} version={previous} onInvalid={() => previous && void markInvalidLink(submission, previous)} />
                    </div>
                    {submission.versions.length > 2 && <details><summary>Tất cả {submission.versions.length} versions</summary>
                      {submission.versions.map((version) => <VersionCard key={version.id} title={`Version ${version.version_number}`} version={version} onInvalid={() => void markInvalidLink(submission, version)} />)}
                    </details>}
                  </details>
                </td>
              </tr>
            </Fragment>
          })}
        </tbody></table></div>
      )}
    </article>
  )
}

function VersionCard({ title, version, onInvalid }: { title: string; version: VersionRow | null; onInvalid: () => void }) {
  if (!version) return <div className="panel"><h3>{title}</h3><p className="muted">Không có version trước.</p></div>
  const safeLink = version.kind === 'link' && isSafeHttpUrl(version.content_value)

  return <div className="panel">
    <div className="panel-header">
      <div><h3>{title}</h3><span className="role-chip">{stageLabels[version.stage]}</span></div>
      <small>{formatDate(version.created_at)}</small>
    </div>
    {version.kind === 'link' ? (
      safeLink ? <><a href={version.content_value} target="_blank" rel="noopener noreferrer">Mở link an toàn</a><p className="muted small">{version.content_value}</p></>
        : <><div className="notice error">Link không hợp lệ hoặc không dùng HTTP(S).</div><button onClick={onInvalid}>Đánh dấu exception</button></>
    ) : <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{version.content_value}</pre>}
    {version.creator_note && <p><strong>Creator note:</strong> {version.creator_note}</p>}
  </div>
}
